import { useState, useEffect } from "react";
import { useOperationsStore } from "./operationsStore";
import {
  Operation,
  CreateOperationParams,
  UpdateOperationParams,
  OPERATION_SOURCE_TYPE,
  UNIT_COST_CALCULATION,
} from "@forgepoint/shared";
import { Archive, ArchiveRestore, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { DataTableBadge } from "@/components/ui/data-table-badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CurrencyInput } from "@/components/CurrencyInputDecimal";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { calculateProductionRate } from "./operationRateUtils";

interface OperationEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  operationId?: string | null;
  onSave?: (operation: Operation) => void;
  /** Called after the operation is archived from the sheet. */
  onArchived?: (operationId: string) => void;
  /** Disables saving for users without the operations.edit permission. */
  readOnly?: boolean;
  /** Seeds the form fields when the sheet opens in create mode. */
  initialValues?: Partial<CreateOperationParams>;
}

/**
 * Slide-over form for creating an operation or editing an existing one.
 * @param props - The open state, the operation to edit, seeded create values and save callback
 * @returns The operation editor sheet
 */
export default function OperationEditorSheet({
  open,
  onOpenChange,
  operationId,
  onSave,
  onArchived,
  readOnly = false,
  initialValues,
}: OperationEditorSheetProps) {
  const {
    operations,
    operationLoadingStates,
    fetchOperation,
    createOperation,
    updateOperation,
    setOperationArchived,
  } = useOperationsStore();
  const epicorPrimitiveSupportEnabled = useFeatureFlag(
    "epicor-primitive-support",
  );

  const [formData, setFormData] = useState<CreateOperationParams>(
    buildCreateFormData(initialValues),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);

  const isEditMode = Boolean(operationId);
  const operation = operationId ? operations.get(operationId) : null;
  const isSynced = Boolean(operation?.externalMappings?.length);
  const isReadOnly = readOnly || isSynced;
  const isArchived = Boolean(operation?.archivedAt);
  const isLoadingArchive = operationId
    ? Boolean(operationLoadingStates.get(operationId)?.isLoadingArchive)
    : false;
  const canArchive = Boolean(operation) && !readOnly;

  useEffect(() => {
    if (!open || operationId) {
      return;
    }
    setFormData(buildCreateFormData(initialValues));
  }, [open, operationId, initialValues]);

  useEffect(() => {
    if (!open || !operationId) {
      return;
    }

    const loadOperation = async () => {
      const existingOperation = operations.get(operationId);
      if (existingOperation) {
        setFormData({
          name: existingOperation.name,
          description: existingOperation.description || "",
          cost: existingOperation.cost,
          burdenRate: existingOperation.burdenRate ?? null,
          setupRate: existingOperation.setupRate ?? null,
          operationSourceType:
            existingOperation.operationSourceType ||
            OPERATION_SOURCE_TYPE.INSIDE,
          unitCostCalculation:
            existingOperation.unitCostCalculation || UNIT_COST_CALCULATION.TIME,
        });
        return;
      }

      setIsLoading(true);
      try {
        await fetchOperation(operationId);
      } finally {
        setIsLoading(false);
      }
    };

    loadOperation();
  }, [open, operationId, operations, fetchOperation]);

  useEffect(() => {
    if (operation) {
      setFormData({
        name: operation.name,
        description: operation.description || "",
        cost: operation.cost,
        burdenRate: operation.burdenRate ?? null,
        setupRate: operation.setupRate ?? null,
        operationSourceType:
          operation.operationSourceType || OPERATION_SOURCE_TYPE.INSIDE,
        unitCostCalculation:
          operation.unitCostCalculation || UNIT_COST_CALCULATION.TIME,
      });
    }
  }, [operation]);

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.cost) {
      toast.error("Name and cost are required");
      return;
    }

    setIsSaving(true);
    try {
      if (isEditMode && operationId) {
        const updateData: UpdateOperationParams = {};
        if (operation && formData.name !== operation.name) {
          updateData.name = formData.name;
        }
        if (operation && formData.description !== operation.description) {
          updateData.description = formData.description || null;
        }
        if (operation && formData.cost !== operation.cost) {
          updateData.cost = formData.cost;
        }
        const burdenRate = formData.burdenRate || null;
        if (operation && burdenRate !== (operation.burdenRate ?? null)) {
          updateData.burdenRate = burdenRate;
        }
        const setupRate = formData.setupRate || null;
        if (operation && setupRate !== (operation.setupRate ?? null)) {
          updateData.setupRate = setupRate;
        }
        if (
          operation &&
          formData.operationSourceType !== operation.operationSourceType
        ) {
          updateData.operationSourceType = formData.operationSourceType;
        }
        if (
          operation &&
          formData.unitCostCalculation !== operation.unitCostCalculation
        ) {
          updateData.unitCostCalculation = formData.unitCostCalculation;
        }

        const { operation: result, error: updateError } = await updateOperation(
          operationId,
          updateData,
        );
        if (result) {
          toast.success("Operation updated successfully");
          onOpenChange(false);
          onSave?.(result);
        } else {
          toast.error(updateError ?? "Failed to update operation");
        }
      } else {
        const { operation: result, error: createError } =
          await createOperation(formData);
        if (result) {
          toast.success("Operation created successfully");
          onOpenChange(false);
          onSave?.(result);
        } else {
          toast.error(createError ?? "Failed to create operation");
        }
      }
    } catch (_error) {
      toast.error(
        isEditMode
          ? "Failed to update operation"
          : "Failed to create operation",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetArchived = async (archived: boolean) => {
    if (!operationId) {
      return;
    }

    const { operation: result, error } = await setOperationArchived(
      operationId,
      archived,
    );
    if (!result) {
      toast.error(error);
      return;
    }

    toast.success(archived ? "Operation archived" : "Operation unarchived");
    if (archived) {
      onArchived?.(operationId);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setFormData(buildCreateFormData(initialValues));
    }
    onOpenChange(newOpen);
  };

  const title = isEditMode ? "Edit Operation" : "Create New Operation";
  const description = isEditMode
    ? isSynced
      ? "This operation is managed by an integration and is read-only in Forgepoint"
      : "Update operation information"
    : "Add a new operation to your system";
  const saveButtonText = isEditMode ? "Save Changes" : "Create Operation";

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="w-[600px] sm:max-w-[600px]">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <SheetTitle>{title}</SheetTitle>
            {isArchived && (
              <DataTableBadge color="amber">Archived</DataTableBadge>
            )}
          </div>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="operation-name">Name *</Label>
              <Input
                id="operation-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Operation name"
                disabled={isReadOnly}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="operation-description">Description</Label>
              <Textarea
                id="operation-description"
                value={formData.description || ""}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Operation description (optional)"
                rows={3}
                disabled={isReadOnly}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="operation-source-type">Outside Operation</Label>
                <div className="text-sm text-muted-foreground">
                  Performed by an external vendor
                </div>
              </div>
              <Switch
                id="operation-source-type"
                checked={
                  formData.operationSourceType === OPERATION_SOURCE_TYPE.OUTSIDE
                }
                disabled={isReadOnly}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    operationSourceType: checked
                      ? OPERATION_SOURCE_TYPE.OUTSIDE
                      : OPERATION_SOURCE_TYPE.INSIDE,
                  })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="costing-method">Costing Method</Label>
              <Select
                value={formData.unitCostCalculation}
                disabled={isReadOnly}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    unitCostCalculation:
                      value === UNIT_COST_CALCULATION.PER_UNIT
                        ? UNIT_COST_CALCULATION.PER_UNIT
                        : UNIT_COST_CALCULATION.TIME,
                  })
                }
              >
                <SelectTrigger id="costing-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNIT_COST_CALCULATION.TIME}>
                    Hourly Rate
                  </SelectItem>
                  <SelectItem value={UNIT_COST_CALCULATION.PER_UNIT}>
                    Per Unit
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="operation-cost">
                {formData.unitCostCalculation === UNIT_COST_CALCULATION.PER_UNIT
                  ? "Unit Cost *"
                  : "Production Labor Per Hour *"}
              </Label>
              <CurrencyInput
                id="operation-cost"
                prefix="$"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e || "" })}
                placeholder="0.00"
                disabled={isReadOnly}
              />
            </div>
            {formData.unitCostCalculation === UNIT_COST_CALCULATION.TIME && (
              <>
                {epicorPrimitiveSupportEnabled && (
                  <div className="grid gap-2">
                    <Label htmlFor="operation-burden-rate">
                      Production Burden Per Hour
                    </Label>
                    <CurrencyInput
                      id="operation-burden-rate"
                      prefix="$"
                      value={formData.burdenRate ?? ""}
                      onChange={(value) =>
                        setFormData({ ...formData, burdenRate: value })
                      }
                      placeholder="0.00"
                      disabled={isReadOnly}
                    />
                  </div>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="operation-total-production-rate">
                    Total Production Rate Per Hour
                  </Label>
                  <CurrencyInput
                    id="operation-total-production-rate"
                    prefix="$"
                    value={calculateProductionRate(formData)}
                    disabled
                  />
                </div>
                {epicorPrimitiveSupportEnabled && (
                  <div className="grid gap-2">
                    <Label htmlFor="operation-setup-rate">
                      Setup Rate Per Hour
                    </Label>
                    <CurrencyInput
                      id="operation-setup-rate"
                      prefix="$"
                      value={formData.setupRate ?? ""}
                      onChange={(value) =>
                        setFormData({ ...formData, setupRate: value })
                      }
                      placeholder="Uses total production rate"
                      disabled={isReadOnly}
                    />
                    <div className="text-sm text-muted-foreground">
                      Leave blank to use the total production rate.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <SheetFooter>
          {canArchive && (
            <Button
              variant="outline"
              className="sm:mr-auto"
              disabled={isLoadingArchive || isSaving || isLoading}
              onClick={() =>
                isArchived
                  ? handleSetArchived(false)
                  : setIsArchiveDialogOpen(true)
              }
            >
              {isArchived ? (
                <ArchiveRestore className="mr-2 h-4 w-4" />
              ) : (
                <Archive className="mr-2 h-4 w-4" />
              )}
              {isArchived ? "Unarchive" : "Archive"}
              {isLoadingArchive && (
                <Loader className="ml-2 h-4 w-4 animate-spin" />
              )}
            </Button>
          )}
          <SheetClose asChild>
            <Button variant="outline" disabled={isSaving || isLoading}>
              Cancel
            </Button>
          </SheetClose>
          <Button
            onClick={handleSave}
            disabled={isReadOnly || isSaving || isLoading}
          >
            {isSaving && <Loader className="mr-2 h-4 w-4 animate-spin" />}
            {saveButtonText}
          </Button>
        </SheetFooter>
      </SheetContent>

      <AlertDialog
        open={isArchiveDialogOpen}
        onOpenChange={setIsArchiveDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Operation</AlertDialogTitle>
            <AlertDialogDescription>
              Archive "{operation?.name}"? It is hidden from operation pickers.
              Items and quotes that use it do not change. You can unarchive it
              later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleSetArchived(true)}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}

/**
 * Builds the create-mode form state, applying any seeded values.
 * @param seed - The values to prefill, if any
 * @returns The create-mode form data
 */
function buildCreateFormData(
  seed?: Partial<CreateOperationParams>,
): CreateOperationParams {
  return {
    name: seed?.name ?? "",
    description: seed?.description ?? "",
    cost: seed?.cost ?? "",
    burdenRate: seed?.burdenRate ?? null,
    setupRate: seed?.setupRate ?? null,
    operationSourceType:
      seed?.operationSourceType ?? OPERATION_SOURCE_TYPE.INSIDE,
    unitCostCalculation:
      seed?.unitCostCalculation ?? UNIT_COST_CALCULATION.TIME,
  };
}
